import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateDatasetSchemaVersionRequest,
  CreateDatasetSchemaVersionResponse,
  GetDatasetSchemaVersionsRequest,
  GetDatasetSchemaVersionsResponse,
  UpdateDatasetSchemaVersionRequest,
  UpdateDatasetSchemaVersionResponse,
} from '@mai/platform-api/src/routes/dataset-schema-versions.schema';

import { type QueryOptions, apiClient } from './axios';

// API functions
export const fetchDatasetSchemaVersions = async (
  params?: GetDatasetSchemaVersionsRequest,
): Promise<GetDatasetSchemaVersionsResponse> => {
  const response = await apiClient.get('/dataset-schema-versions', { params });
  return response.data;
};

export const fetchDatasetSchemaVersionsByDatasetId = async (
  datasetId: string,
): Promise<GetDatasetSchemaVersionsResponse> => {
  return fetchDatasetSchemaVersions({ datasetIds: [datasetId] });
};

export const createDatasetSchemaVersion = async (
  data: CreateDatasetSchemaVersionRequest,
): Promise<CreateDatasetSchemaVersionResponse> => {
  const response = await apiClient.post('/dataset-schema-versions', data);
  return response.data;
};

export const updateDatasetSchemaVersion = async (
  datasetSchemaVersionId: string,
  data: UpdateDatasetSchemaVersionRequest,
): Promise<UpdateDatasetSchemaVersionResponse> => {
  const response = await apiClient.patch(
    `/dataset-schema-versions/${datasetSchemaVersionId}`,
    data,
  );
  return response.data;
};

// React Query hooks
export const useDatasetSchemaVersions = (
  params?: GetDatasetSchemaVersionsRequest,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['dataset-schema-versions', params],
    queryFn: () => fetchDatasetSchemaVersions(params),
    enabled: options?.enabled,
    retry: options?.retry,
  });
};

export const useDatasetSchemaVersionsByDatasetId = (
  datasetId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['dataset-schema-versions', 'by-dataset', datasetId],
    queryFn: () => fetchDatasetSchemaVersionsByDatasetId(datasetId!),
    enabled: !!datasetId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useCreateDatasetSchemaVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDatasetSchemaVersion,
    onSuccess: (data) => {
      // Invalidate schema version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['dataset-schema-versions'],
      });
      // Also invalidate specific dataset queries
      void queryClient.invalidateQueries({
        queryKey: ['dataset-schema-versions', 'by-dataset', data.datasetId],
      });
    },
  });
};

export const useUpdateDatasetSchemaVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      datasetSchemaVersionId,
      data,
    }: {
      datasetSchemaVersionId: string;
      data: UpdateDatasetSchemaVersionRequest;
    }) => updateDatasetSchemaVersion(datasetSchemaVersionId, data),
    onSuccess: (data) => {
      // Invalidate schema version queries to refetch the data
      void queryClient.invalidateQueries({
        queryKey: ['dataset-schema-versions'],
      });
      // Also invalidate specific dataset queries
      void queryClient.invalidateQueries({
        queryKey: ['dataset-schema-versions', 'by-dataset', data.datasetId],
      });
    },
  });
};
